import {
	useEffect, useId, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import apiFetch from '@wordpress/api-fetch';
import { useAuiState } from '@assistant-ui/react';
import { cn } from '@uconnect/assets/lib/utils';
import { Badge } from '@uconnect/assets/components/ui/badge';
import { Button } from '@uconnect/assets/components/ui/button';
import Icon from '@uconnect/assets/components/ui/icon';
import { useIsTruncated } from '@uconnect/assets/hooks/useChipFit';
import { useNewChat } from '../RuntimeProvider';
import { setPersonalization } from '../hooks/usePersonalization';
import type { PersonalizationSummary, Suggestion } from '../../../common/js/types';

type PanelStatus = 'open' | 'submitting' | 'subscribed' | 'dismissed';

type Chip = {
	key: string;
	id: number;
	name: string;
	taxonomy: string;
};

// Panel outcomes keyed by message ID, including an in-flight save. Kept outside the component
// because the bound AssistantMessage can remount (it is recreated when its props change): a
// dismissed or completed panel must not reappear, and a remount mid-save must not re-enable the
// button. Listeners let a remounted panel re-render when the original instance's save settles.
const settledPanels = new Map< string, PanelStatus >();
const settledListeners: Set< () => void > = new Set();
const trackedImpressions = new Set< string >();

function setSettledPanel( messageId: string, status: PanelStatus | null ) {
	if ( status ) {
		settledPanels.set( messageId, status );
	} else {
		settledPanels.delete( messageId );
	}
	settledListeners.forEach( ( listener ) => listener() );
}

function subscribeToSettledPanels( listener: () => void ): () => void {
	settledListeners.add( listener );
	return () => settledListeners.delete( listener );
}

function sendEvent( name: string, params: Record< string, unknown > ) {
	window.uconnect?.analytics?.send_event?.( name, params );
}

/**
 * Flattens a suggestion into one chip list, communities first. Terms whose kind this site cannot
 * subscribe to (absent from `subscribeTaxonomies`) are dropped so one stale term cannot fail the
 * whole request.
 *
 * @param {Suggestion} suggestion The agent's suggestion frame.
 * @return {Chip[]} Deduplicated chips. Not capped: the agent sets how many terms a panel offers,
 *                  and puts each one on cooldown when it sends them, so every term must be shown.
 */
function toChips( suggestion: Suggestion ): Chip[] {
	const taxonomies = window.agentConfig?.subscribeTaxonomies ?? {};
	const terms = [
		...suggestion.communities.map( ( term ) => ( { ...term, kind: 'community' } ) ),
		...suggestion.categories.map( ( term ) => ( { ...term, kind: term.group ?? '' } ) ),
	];

	const chips = terms
		.filter( ( term ) => taxonomies[ term.kind ] )
		.map( ( term ) => ( {
			key: `${ taxonomies[ term.kind ] }:${ term.id }`,
			id: term.id,
			name: term.name,
			taxonomy: taxonomies[ term.kind ],
		} ) );

	return chips.filter( ( chip, index ) => chips.findIndex( ( other ) => other.key === chip.key ) === index );
}

function toSubscriptions( chips: Chip[] ): Record< string, number[] > {
	return chips.reduce< Record< string, number[] > >( ( subscriptions, chip ) => ( {
		...subscriptions,
		[ chip.taxonomy ]: [ ...( subscriptions[ chip.taxonomy ] ?? [] ), chip.id ],
	} ), {} );
}

function joinKeys( chips: Chip[] ): string {
	return chips.map( ( chip ) => chip.key ).join( ',' );
}

/**
 * Finds the chat composer's input for the thread this element belongs to, so focus has
 * somewhere sensible to land when the panel closes.
 *
 * @param {HTMLElement|null} from Element inside the thread to search outward from.
 * @return {HTMLElement|null} The composer's textarea, or null if none is found.
 */
function findComposerInput( from: HTMLElement | null ): HTMLElement | null {
	let node = from?.parentElement ?? null;
	while ( node ) {
		const input = node.querySelector< HTMLElement >( 'textarea[name="message-input"]' );
		if ( input ) {
			return input;
		}
		node = node.parentElement;
	}
	return null;
}

/**
 * A toggleable suggestion chip. Long names are cut off with an ellipsis, and the full name is
 * offered as a tooltip only when that happens.
 *
 * @param {Object}   props
 * @param {Chip}     props.chip       The term this chip offers.
 * @param {boolean}  props.isSelected Whether the term will be subscribed.
 * @param {boolean}  [props.disabled] Disables the chip while a request is in flight.
 * @param {Function} props.onToggle   Called when the chip is clicked.
 */
function SuggestionChip( {
	chip, isSelected, disabled, onToggle,
}: {
	chip: Chip;
	isSelected: boolean;
	disabled?: boolean;
	onToggle: () => void;
} ) {
	// `text-overflow: ellipsis` only takes effect on a block container's own inline content, not on
	// a flex container's children, so the truncating styles live on this inner span.
	const textRef = useRef< HTMLSpanElement >( null );
	const isTruncated = useIsTruncated( textRef, [ chip.name ] );

	return (
		// Every visual rule is `!` because the theme's global `.button` styles otherwise win; that
		// includes the font, which is reset to the surrounding text's. Selected uses the site's own
		// `main` scale so it stands out in the client's brand colours rather than a fixed accent.
		<Badge
			asChild
			variant="outline"
			className={ cn(
				'inline-flex! shrink-0! items-center gap-1.5! h-auto! max-w-[240px]! m-0! px-3! py-1!',
				'rounded-full! border! shadow-none! text-sm! font-medium! leading-tight!',
				'[font-family:inherit]! normal-case! tracking-normal!',
				'cursor-pointer transition-colors disabled:cursor-default disabled:opacity-60',
				isSelected && [
					'bg-main-100! border-main-300! text-main-900!',
					'hover:bg-main-200!',
				],
				! isSelected && [
					'bg-white! border-dashed! border-border!',
					'text-muted-foreground! hover:text-foreground!',
				],
			) }
		>
			<button
				type="button"
				aria-pressed={ isSelected }
				title={ isTruncated ? chip.name : undefined }
				disabled={ disabled }
				onClick={ onToggle }
			>
				<Icon name={ isSelected ? 'check' : 'xmark' } className="size-3 stroke-2 shrink-0" />
				<span
					ref={ textRef }
					className="block! min-w-0! overflow-hidden! text-ellipsis! whitespace-nowrap!"
				>
					{ chip.name }
				</span>
			</button>
		</Badge>
	);
}

/**
 * Offers one-click subscription to the communities and categories the agent noticed the user
 * engaging with. Rendered below the Sources box once the answer has finished streaming.
 *
 * Closing is local-only: the agent puts every shown term on cooldown when it sends the
 * suggestion, so there is nothing to tell the server.
 */
export default function SuggestionPanel() {
	const { threadId } = useNewChat();
	const messageId = useAuiState( ( s ) => ( s.message as any )?.id as string | undefined );
	const suggestion = useAuiState(
		( s ) => ( s.message as any )?.metadata?.custom?.suggestion as Suggestion | undefined,
	);
	const isStreaming = useAuiState(
		( s ) => Boolean( ( s.message as any )?.metadata?.custom?.isStreaming ),
	);

	// The suggestion only lands on the final yield, after this mounts, so chips are derived rather
	// than captured once; tracking the *deselected* keys keeps every chip selected by default.
	const chips = useMemo( () => ( suggestion ? toChips( suggestion ) : [] ), [ suggestion ] );
	const [ deselectedKeys, setDeselectedKeys ] = useState< Set< string > >( () => new Set() );
	const [ localStatus, setStatus ] = useState< PanelStatus >( 'open' );
	// A settled outcome wins over local state, even if `messageId` resolves late or changes.
	const settledStatus = useSyncExternalStore(
		subscribeToSettledPanels,
		() => ( messageId ? settledPanels.get( messageId ) : undefined ),
	);
	const status = settledStatus || localStatus;
	const [ error, setError ] = useState( '' );
	const [ announcement, setAnnouncement ] = useState( '' );
	const panelRef = useRef< HTMLDivElement >( null );
	const headingId = useId();

	// Every chip is shown, however many lines that takes: the agent puts each suggested term on
	// cooldown, so a hidden one would be withheld from the user without them ever being asked.
	const isVisible = Boolean( window.agentConfig?.isLoggedIn && messageId && chips.length && ! isStreaming );
	const selectedChips = chips.filter( ( chip ) => ! deselectedKeys.has( chip.key ) );

	useEffect( () => {
		if ( ! isVisible || status !== 'open' || trackedImpressions.has( messageId! ) ) {
			return;
		}
		trackedImpressions.add( messageId! );
		sendEvent( 'ai_search_preference_nudge_shown', {
			thread_id: threadId,
			suggested_count: chips.length,
			suggested_terms: joinKeys( chips ),
		} );
	}, [ chips, isVisible, messageId, status, threadId ] );

	if ( ! isVisible || status === 'dismissed' ) {
		return null;
	}

	const settle = ( next: PanelStatus ) => {
		setSettledPanel( messageId!, next );
		setStatus( next );
	};

	const toggleChip = ( key: string ) => {
		setDeselectedKeys( ( prev ) => {
			const next = new Set( prev );
			if ( next.has( key ) ) {
				next.delete( key );
			} else {
				next.add( key );
			}
			return next;
		} );
	};

	const handleDismiss = () => {
		findComposerInput( panelRef.current )?.focus();
		sendEvent( 'ai_search_preference_nudge_dismissed', {
			thread_id: threadId,
			suggested_count: chips.length,
			suggested_terms: joinKeys( chips ),
		} );
		settle( 'dismissed' );
	};

	const handleSubscribe = async () => {
		if ( ! selectedChips.length ) {
			return;
		}
		setError( '' );
		settle( 'submitting' );

		let response: { personalization?: PersonalizationSummary };
		try {
			response = await apiFetch( {
				path: `${ window.agentConfig?.agentWPApiBase ?? '/uconnect/v1/agent/' }preferences/subscribe`,
				method: 'POST',
				data: { subscriptions: toSubscriptions( selectedChips ) },
			} );
		} catch {
			setSettledPanel( messageId!, null );
			setStatus( 'open' );
			setError( 'We couldn’t save your preferences. Please try again.' );
			return;
		}

		// Refreshes the "AI Search is now personalized to you" banner shown on the next new search.
		if ( response.personalization ) {
			setPersonalization( response.personalization );
		}

		// The cached JWT still carries the old subscriptions; clearing it makes the next
		// question mint a fresh one, so the agent personalizes with (and stops suggesting) these terms.
		if ( window.agentConfig ) {
			window.agentConfig.jwtToken = '';
		}

		sendEvent( 'ai_search_preference_nudge_subscribed', {
			thread_id: threadId,
			suggested_count: chips.length,
			subscribed_count: selectedChips.length,
			subscribed_terms: joinKeys( selectedChips ),
		} );
		setAnnouncement( `You’re now following ${ selectedChips.map( ( chip ) => chip.name ).join( ', ' ) }.` );
		settle( 'subscribed' );
	};

	const preferencesUrl = window.agentConfig?.contentPreferencesUrl;

	return (
		<div ref={ panelRef } className="mt-3!">
			<div role="status" className="sr-only">{ announcement }</div>

			{ status === 'subscribed' ? (
				<p className="flex! items-center gap-2 text-sm! m-0! p-2! bg-main-100! rounded-sm">
					<Icon name="check" className="size-4 shrink-0" />
					<span className="flex! items-center justify-between gap-2 flex-wrap grow">
						Preferences updated!
						{ preferencesUrl && (
							<>
								{ ' ' }
								<a href={ preferencesUrl } className="underline! underline-offset-2">
									Manage preferences
								</a>
							</>
						) }
					</span>
				</p>
			) : (
				<section
					aria-labelledby={ headingId }
					className="relative p-3! text-sm border! border-border! rounded"
				>
					<Button
						type="button"
						variant="ghost"
						size="icon-sm"
						onClick={ handleDismiss }
						aria-label="Dismiss suggestions"
						className="absolute! top-1.5 right-1.5"
					>
						<Icon name="xmark" className="size-3.5 stroke-2 text-muted-foreground!" />
					</Button>

					<h3 id={ headingId } className="text-base! font-semibold! m-0! pr-8!">
						Follow related topics
					</h3>
					<p className="m-0! mt-0.5! text-muted-foreground!">
						Personalize your experience with the topics that matter to you.
					</p>

					<ul className="flex flex-wrap gap-2 list-none! m-0! mt-3! p-0!">
						{ chips.map( ( chip ) => (
							<li key={ chip.key } className="m-0! min-w-0!">
								<SuggestionChip
									chip={ chip }
									isSelected={ ! deselectedKeys.has( chip.key ) }
									disabled={ status === 'submitting' }
									onToggle={ () => toggleChip( chip.key ) }
								/>
							</li>
						) ) }
					</ul>

					{ error && (
						<p role="alert" className="m-0! mt-3! text-destructive!">{ error }</p>
					) }

					<Button
						type="button"
						size="sm"
						onClick={ handleSubscribe }
						disabled={ ! selectedChips.length || status === 'submitting' }
						className="mt-3! w-full"
					>
						{ status === 'submitting' ? 'Updating…' : 'Update preferences' }
					</Button>
				</section>
			) }
		</div>
	);
}
